import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import type { ApiResponseMailAlias } from '../../../types/api/ApiResponseMailAlias';

interface MailAliasCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  mailDomainId: number;
  domainName: string;
  onSuccess: () => void;
}

const SOURCE_REGEX = /^(@|[A-Za-z0-9._%+-]+)$/;
const SOURCE_MAX_LENGTH = 40;
const DESTINATION_MAX_LENGTH = 2048;

function MailAliasCreateModal({ isOpen, onClose, mailDomainId, domainName, onSuccess }: MailAliasCreateModalProps) {
  const apiFetch = useApiFetch();
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedSource = source.trim();
  // An empty source (or a typed '@') is the domain-wide catch-all, which the backend stores as '@'.
  const canonicalSource = trimmedSource === '' ? '@' : trimmedSource;
  const isCatchAll = canonicalSource === '@';
  const trimmedDestination = destination.trim();
  const isSourceValid = canonicalSource.length <= SOURCE_MAX_LENGTH && SOURCE_REGEX.test(canonicalSource);
  const isDestinationValid = trimmedDestination.length > 0 && trimmedDestination.length <= DESTINATION_MAX_LENGTH;
  const isFormValid = isSourceValid && isDestinationValid;

  const previewAddress = isCatchAll ? `@${domainName}` : `${canonicalSource}@${domainName}`;

  const resetAndClose = () => {
    setSource('');
    setDestination('');
    setError(null);
    setFieldErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      const response = await apiFetch('/emails/aliases', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify({
          mail_domain_id: mailDomainId,
          source: canonicalSource,
          destination: trimmedDestination,
        }),
      });

      const result = (await response.json().catch(() => null)) as
        | (ApiResponseMailAlias & { data?: Record<string, string> })
        | null;

      if (response.status === 201 && result?.status === 'success') {
        setSource('');
        setDestination('');
        setFieldErrors({});
        setError(null);
        onSuccess();
        onClose();
        return;
      }

      if (response.status === 422 && result?.status === 'fail' && result.data) {
        const errors: Record<string, string> = {};
        for (const [key, val] of Object.entries(result.data)) {
          errors[key] = String(val);
        }
        setFieldErrors(errors);
      } else {
        setError(result?.message ?? `HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create alias');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="small" isOpen={isOpen} onClose={resetAndClose} aria-label="Create alias">
      <ModalHeader title="Create alias" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Creation failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Form>
          <FormGroup label="Source" fieldId="create-alias-source">
            <TextInput
              id="create-alias-source"
              value={source}
              onChange={(_event, value) => {
                setSource(value);
                if (fieldErrors.source) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.source;
                    return next;
                  });
                }
              }}
              validated={fieldErrors.source || !isSourceValid ? 'error' : 'default'}
            />
            {fieldErrors.source ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.source}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : !isSourceValid ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">
                    Use letters, numbers, dots, hyphens, underscores, plus signs, or percent signs (max 40), or leave it
                    empty for a domain-wide catch-all.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    Email address: <strong>{previewAddress}</strong>
                    {isCatchAll && ' (catch-all for the whole domain)'}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          <FormGroup label="Destination" isRequired fieldId="create-alias-destination">
            <TextArea
              id="create-alias-destination"
              value={destination}
              onChange={(_event, value) => {
                setDestination(value);
                if (fieldErrors.destination) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.destination;
                    return next;
                  });
                }
              }}
              rows={3}
              resizeOrientation="vertical"
              aria-label="Destination addresses"
              validated={fieldErrors.destination || (destination && !isDestinationValid) ? 'error' : 'default'}
            />
            {fieldErrors.destination ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.destination}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    Comma-separated list of email addresses. Use multiple destinations to fan out delivery.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isDisabled={!isFormValid || isSubmitting}
          isLoading={isSubmitting}
        >
          Create
        </Button>
        <Button variant="link" onClick={resetAndClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MailAliasCreateModal;
